import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, Plus, Trash2, CreditCard, Percent, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getPricingSettings, updatePricingSettings, type CreditPackage } from "@/lib/pricingApi";

const PricingSettings = () => {
  const { toast } = useToast();
  const [costPer1k, setCostPer1k] = useState("190");
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load pricing settings on mount
  useEffect(() => {
    loadPricingSettings();
  }, []);

  const loadPricingSettings = async () => {
    try {
      setLoading(true);
      const settings = await getPricingSettings();
      setCostPer1k(settings.costPer1k);
      setPackages(settings.packages);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error Loading Settings",
        description: "Failed to load pricing settings. Using defaults.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePackageChange = (id: string, field: keyof CreditPackage, value: string | number | boolean) => {
    setPackages(packages.map(pkg =>
      pkg.id === id ? { ...pkg, [field]: value } : pkg
    ));
  };

  const addPackage = () => {
    const newId = (Math.max(...packages.map(p => parseInt(p.id))) + 1).toString();
    setPackages([...packages, { id: newId, credits: 100, price: 99, bonus: 0, popular: false }]);
  };

  const removePackage = (id: string) => {
    if (packages.length > 1) {
      setPackages(packages.filter(pkg => pkg.id !== id));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const result = await updatePricingSettings({
        costPer1k,
        packages,
      });

      if (result.success) {
        toast({
          title: "Pricing Updated",
          description: "Credit packages and pricing have been saved successfully.",
        });
      } else {
        throw new Error(result.error || "Failed to save");
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Save Failed",
        description: error instanceof Error ? error.message : "Failed to save pricing settings.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Markup Rate */}
      <Card variant="glass">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Percent className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle>Pricing Markup Rate</CardTitle>
              <CardDescription>Set the markup percentage above Kie.ai base prices</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="cost">Cost per 1,000 Credits (฿)</Label>
                <Input
                  id="cost"
                  type="number"
                  value={costPer1k}
                  onChange={(e) => setCostPer1k(e.target.value)}
                  min="0"
                  step="0.1"
                />
                <p className="text-xs text-muted-foreground">Used for calculating profit per package</p>
              </div>
            </div>
            <div className="flex flex-col justify-center p-4 rounded-xl bg-primary/5 border border-primary/10">
              <p className="text-sm font-medium text-primary mb-1 text-center">Base Cost per 1 Credit</p>
              <p className="text-3xl font-bold text-center">฿{(parseFloat(costPer1k) / 1000).toFixed(4)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Credit Packages */}
      <Card variant="glass">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center">
                <CreditCard className="h-5 w-5 text-secondary" />
              </div>
              <div>
                <CardTitle>Credit Packages</CardTitle>
                <CardDescription>Configure available credit packages for users</CardDescription>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={addPackage} className="gap-2">
              <Plus className="h-4 w-4" />
              Add Package
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Header */}
            <div className="grid grid-cols-12 gap-4 text-sm font-medium text-muted-foreground px-2">
              <div className="col-span-2">Credits</div>
              <div className="col-span-2">Price (฿)</div>
              <div className="col-span-1">Bonus</div>
              <div className="col-span-2 text-center">฿/Credit</div>
              <div className="col-span-2 text-center">Cost & Profit</div>
              <div className="col-span-1 text-center">Pop</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>

            {packages.map((pkg) => {
              const totalCredits = pkg.credits + pkg.bonus;
              const pricePerCredit = pkg.price / totalCredits;
              const actualCost = (pkg.credits * parseFloat(costPer1k)) / 1000;
              const profit = pkg.price - actualCost;
              const profitMargin = (profit / pkg.price) * 100;

              return (
                <div key={pkg.id} className="grid grid-cols-12 gap-4 items-center p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                  <div className="col-span-2">
                    <Input
                      type="number"
                      value={pkg.credits}
                      onChange={(e) => handlePackageChange(pkg.id, "credits", parseInt(e.target.value) || 0)}
                      min="1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      value={pkg.price}
                      onChange={(e) => handlePackageChange(pkg.id, "price", parseInt(e.target.value) || 0)}
                      min="1"
                    />
                  </div>
                  <div className="col-span-1">
                    <Input
                      type="number"
                      value={pkg.bonus}
                      onChange={(e) => handlePackageChange(pkg.id, "bonus", parseInt(e.target.value) || 0)}
                      min="0"
                    />
                  </div>
                  <div className="col-span-2 text-center">
                    <div className="text-sm font-bold">฿{pricePerCredit.toFixed(3)}</div>
                    <div className="text-[10px] text-muted-foreground">per credit</div>
                  </div>
                  <div className="col-span-2 text-center">
                    <div className="text-[10px] text-muted-foreground">Cost: ฿{actualCost.toFixed(2)}</div>
                    <div className={`text-sm font-bold ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      Profit: ฿{profit.toFixed(2)}
                    </div>
                    <div className="text-[10px] opacity-70">({profitMargin.toFixed(1)}%)</div>
                  </div>
                  <div className="col-span-1 flex justify-center">
                    <button
                      onClick={() => handlePackageChange(pkg.id, "popular", !pkg.popular)}
                      className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${pkg.popular ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20" : "bg-muted border border-border hover:bg-muted/80"}`}
                    >
                      <Plus className={`h-3 w-3 ${pkg.popular ? "rotate-45" : ""}`} />
                    </button>
                  </div>
                  <div className="col-span-2 flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removePackage(pkg.id)}
                      className="text-destructive hover:text-destructive h-8 w-8"
                      disabled={packages.length === 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving || loading} className="gap-2">
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save Pricing Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default PricingSettings;
